import { inject, Injectable } from '@angular/core';
import { EMPTY, Observable, of, timer } from 'rxjs';
import {
  catchError,
  defaultIfEmpty,
  exhaustMap,
  filter,
  map,
  switchMap,
  take,
  takeWhile,
} from 'rxjs/operators';
import { ConversationService } from 'src/app/features/conversation/services/conversation.service';
import { ConversationDto } from 'src/app/features/conversation/models/conversation.dto';
import { CrmContactService } from 'src/app/features/contact/services/crm-contact.service';
import { CrmContactDto } from 'src/app/features/contact/models/crm-contact.dto';
import { CallFacadeService } from '../telephony/services/call-facade.service';
import { parseSipPhoneNumber } from '../telephony/utils/parse-sip-phone-number';

/** The lookup is agent-scoped and this agent's segment lands just after the INVITE; poll ~5s. */
const CONVERSATION_POLL_INTERVAL_MS = 300;
const CONVERSATION_POLL_ATTEMPTS = 17;

/**
 * Resolves which CRM contact is calling, shared by every incoming-call surface (Agent Desktop page and
 * the route-independent status toolbar) so both show the same caller.
 */
@Injectable({ providedIn: 'root' })
export class CallerContactResolverService {
  private readonly facade = inject(CallFacadeService);
  private readonly conversationService = inject(ConversationService);
  private readonly crmContactService = inject(CrmContactService);

  /**
   * Contact the backend linked to the call's conversation first (already matched there with the tenant's
   * phone normalisation), the caller's phone number second.
   * @returns The contact or null, at most once and only while the same session is still current.
   */
  resolveCurrentCaller(): Observable<CrmContactDto | null> {
    const session = this.facade.currentSession();
    if (!session) {
      return EMPTY;
    }

    const isSameSession = (): boolean => this.facade.currentSession()?.id === session.id;

    return this.findByConversation(session.conversationUid ?? null, isSameSession).pipe(
      switchMap(contact =>
        contact || !isSameSession()
          ? of(contact)
          : this.findByPhoneNumber(parseSipPhoneNumber(session.remoteUri)),
      ),
      filter(() => isSameSession()),
    );
  }

  private findByPhoneNumber(phoneNumber: string): Observable<CrmContactDto | null> {
    return phoneNumber ? this.crmContactService.getByPhoneNumber(phoneNumber) : of(null);
  }

  private findByConversation(
    conversationUid: string | null,
    isSameSession: () => boolean,
  ): Observable<CrmContactDto | null> {
    if (!conversationUid) {
      return of(null);
    }

    return timer(0, CONVERSATION_POLL_INTERVAL_MS).pipe(
      takeWhile(isSameSession),
      take(CONVERSATION_POLL_ATTEMPTS),
      exhaustMap(() => this.conversationService.getByConversationUid(conversationUid)),
      filter((conversation): conversation is ConversationDto => conversation !== null),
      take(1),
      map(conversation => conversation.contactId ?? null),
      defaultIfEmpty(null),
      switchMap(contactId =>
        contactId === null
          ? of(null)
          : this.crmContactService.getById(contactId).pipe(catchError(() => of(null))),
      ),
    );
  }
}
